import { useCallback, useEffect, useRef } from 'react';

import { useMarkCommentsAsRead } from './useMarkCommentsAsRead';

export const useDebouncedMarkCommentsAsRead = (debounceMs: number = 1000) => {
	const markCommentsAsRead = useMarkCommentsAsRead();
	const pendingCommentIds = useRef<Set<string>>(new Set());
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const beforeUnloadHandlerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(null);

	const debouncedMarkCommentsAsRead = useCallback(
		(commentId: string) => {
			// Add the comment ID to the pending set
			pendingCommentIds.current.add(commentId);

			// Clear any existing timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Set a new timeout to batch and send the request
			timeoutRef.current = setTimeout(() => {
				const commentIdsToMark = Array.from(pendingCommentIds.current);

				// Clear the pending set
				pendingCommentIds.current.clear();

				// Reset the timeout ref
				timeoutRef.current = null;

				// Only call the mutation if there are comments to mark
				if (commentIdsToMark.length > 0) {
					markCommentsAsRead(commentIdsToMark);
				}
			}, debounceMs);
		},
		[markCommentsAsRead, debounceMs],
	);

	// Function to send pending comment IDs
	const sendPendingComments = useCallback(() => {
		const commentIdsToMark = Array.from(pendingCommentIds.current);

		if (commentIdsToMark.length === 0) {
			return;
		}

		// Clear the pending set
		pendingCommentIds.current.clear();

		// Just call the existing mutation function
		markCommentsAsRead(commentIdsToMark);
	}, [markCommentsAsRead]);

	// Beforeunload event handler (e.g. when the user closes the tab or window)
	const handleBeforeUnload = useCallback(
		(_event: BeforeUnloadEvent) => {
			sendPendingComments();
		},
		[sendPendingComments],
	);

	// Set up beforeunload event listener
	useEffect(() => {
		beforeUnloadHandlerRef.current = handleBeforeUnload;
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [handleBeforeUnload]);

	// Flush pending comments and clear timeout
	const flushPendingComments = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		// Send any pending comments
		sendPendingComments();
	}, [sendPendingComments]);

	return {
		debouncedMarkCommentsAsRead,
		flushPendingComments,
	};
};
